import React, { useCallback, useEffect, useRef, useState } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  Buttons,
  ModalContent,
  EmailSection,
  Text,
  EmailName,
  Payment,
  Check,
  ButtonSection,
  Label,
  LabelSection,
  AddCCBlock,
  InputSection
} from './styles';
import Button from '../../button';
import { CustomerDetailsInterface } from '../../../interfaces/CustomerInterface';
import { getCustomerList } from '../../../services/customerServices';
import { dispatchInvoiceError, sendInvoiceToCustomers } from '../../../services/invoiceService';
import { ROUTES } from '../../../routes/urls';
import { isDev, isEmail, isEmpty } from '../../../helpers/common';
import { captureException } from '../../../services/logService';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { INVOICE_ANALYTICS } from '../../../global/analyticsConstants';
import { Dropdown, DropdownItem } from '../../dropdown';
import { useMobileDevice } from '../../../global/useMobile';
import { getCompanySettingIntegration } from '../../../services/settingServices';
import { BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import Input from '../../input';
import TextArea from '../../input/textArea';
import ModalHeader from '../modalHeader';
import CheckboxInput from '../../input/checkboxInput';
interface Props {
  onClose: () => void;
  invoiceId: string;
  workspaceId: string;
}

const SendInvoiceModal: React.FC<Props> = (props) => {
  const { invoiceId, workspaceId } = props;
  //Refs
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const recipientRef = useRef<{
    closeDropdown: () => void;
  }>(null);
  const ccRef = useRef<{
    closeDropdown: () => void;
  }>(null);
  //States
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [carbonCopyEmails, setCarbonCopyEmails] = useState<string[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [subjectInput, setSubjectInput] = useState('');
  const [searchRecipient, setSearchRecipient] = useState('');
  const [searchCC, setSearchCC] = useState('');
  const [customerWithEmailList, setCustomerWithEmailList] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentEnabled, setPaymentEnabled] = useState(false);
  const [isOnlinePayment, setIsOnlinePayment] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCCDropdownOpen, setIsCCDropdownOpen] = useState(false);
  const [showCCField, setShowCCField] = useState(false);
  const [isIncludePDF, setIsIncludePDF] = useState(false);
  const isMobile = useMobileDevice();
  const isMinWidth = isMobile ? Math.max(window.innerWidth - 65) : 640;

  const handleTextareaInput = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      if (textarea.scrollHeight > textarea.clientHeight) {
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }
  }, []);

  const onChangeRecipientInput = useCallback((value: string) => {
    setSearchRecipient(value);
  }, []);

  const onChangeCCInput = useCallback((value: string) => {
    setSearchCC(value);
  }, []);

  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(workspaceId)) {
        setLoading(true);
        let customersList = await getCustomerList();
        customersList = customersList?.map((x: CustomerDetailsInterface) => {
          return { ...x, label: `${x?.companyName} - ${x?.email}`, value: x?.email };
        });
        // Deduplicate by email (value)
        const seenEmails = new Set<string>();
        const uniqueCustomers = (customersList || []).filter((customer: { value?: string }) => {
          const email = customer?.value?.toLowerCase?.() || '';
          if (!email || seenEmails.has(email)) return false;
          seenEmails.add(email);
          return true;
        });
        setCustomerWithEmailList(uniqueCustomers);
        const result = await getCompanySettingIntegration(workspaceId);
        setPaymentEnabled(result?.stripe_connected);
      }
    } catch (error) {
      captureException(error);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    handleTextareaInput();
    loadData();
  }, [handleTextareaInput, loadData]);

  const onClickClose = useCallback(() => {
    props?.onClose();
    setMessageInput('');
    setIsOnlinePayment(false);
    setCustomerWithEmailList([]);
    setPaymentEnabled(false);
  }, [props]);

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        if (isEmpty(recipientEmails) || recipientEmails.length === 0) {
          dispatchInvoiceError('Please enter at least one recipient.');
          return;
        }
        const invalidEmail = recipientEmails?.find((email) => !isEmail(email));
        if (invalidEmail) {
          dispatchInvoiceError('Please enter valid email address.');
          return;
        }
        const payload = {
          emails: recipientEmails,
          onlinePayment: isOnlinePayment,
          message: messageInput,
          subject: subjectInput,
          cc: carbonCopyEmails,
          invoiceId: invoiceId,
          isPdf: isIncludePDF,
          isDev: isDev()
        };
        const result = await sendInvoiceToCustomers(payload);
        if (result) {
          trackAnalyticActivity(INVOICE_ANALYTICS.SENT);
          onClickClose();
        }
      } catch (error) {
        captureException(error);
      } finally {
        setLoading(false);
      }
    },
    [
      recipientEmails,
      isOnlinePayment,
      messageInput,
      subjectInput,
      carbonCopyEmails,
      invoiceId,
      isIncludePDF,
      onClickClose
    ]
  );

  // Handle item selection
  const selectRecipentEmail = useCallback(
    (email: string) => {
      if (!recipientEmails.includes(email)) {
        setRecipientEmails([...recipientEmails, email]);
      } else {
        const updatedRecipient = recipientEmails?.filter((item) => item !== email) || [];
        setRecipientEmails(updatedRecipient);
      }
      setSearchCC('');
      setSearchRecipient('');
    },
    [recipientEmails]
  );

  const selectCarbonEmail = useCallback(
    (email: string) => {
      if (!carbonCopyEmails.includes(email)) {
        setCarbonCopyEmails([...carbonCopyEmails, email]);
      } else {
        const updatedCarbonEmails = carbonCopyEmails?.filter((item) => item !== email) || [];
        setCarbonCopyEmails(updatedCarbonEmails);
      }
      setSearchCC('');
      setSearchRecipient('');
    },
    [carbonCopyEmails]
  );

  // Update handleKeyDown to handle Enter key
  const handleRecipientKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && searchRecipient === '' && recipientEmails.length > 0) {
        setRecipientEmails(recipientEmails.slice(0, -1)); // Remove last email completely
      }

      if (e.key === 'Enter' && searchRecipient.trim() !== '') {
        // Check if searchTerm matches a customer email
        const foundCustomer =
          customerWithEmailList?.find((item) => item.value?.toLowerCase() === searchRecipient?.trim().toLowerCase()) ??
          null;

        if (
          foundCustomer &&
          !recipientEmails.includes(foundCustomer.value) &&
          !carbonCopyEmails.includes(foundCustomer.value)
        ) {
          setRecipientEmails([...recipientEmails, foundCustomer.value]);
          setSearchRecipient('');
          e.preventDefault();
          return;
        } else if (foundCustomer) {
          dispatchInvoiceError('Email is already added in Recipient or CC.');
          return;
        }

        // Add any input as email (even if not valid)
        if (!recipientEmails.includes(searchRecipient.trim())) {
          if (!isEmail(searchRecipient)) {
            dispatchInvoiceError('Please add proper email');
            return;
          }
          setRecipientEmails([...recipientEmails, searchRecipient.trim()]);
          setSearchRecipient('');
          e.preventDefault();
          return;
        }
      }
    },
    [carbonCopyEmails, customerWithEmailList, recipientEmails, searchRecipient]
  );

  // Update handleKeyDown to handle Enter key
  const handleCCKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && searchCC === '' && carbonCopyEmails.length > 0) {
        setCarbonCopyEmails(carbonCopyEmails.slice(0, -1));
      }

      if (e.key === 'Enter' && searchCC.trim() !== '') {
        // Check if searchTerm matches a customer email
        const foundCustomer =
          customerWithEmailList?.find((item) => item.value?.toLowerCase() === searchCC?.trim().toLowerCase()) ?? null;

        if (
          foundCustomer &&
          !recipientEmails.includes(foundCustomer.value) &&
          !carbonCopyEmails.includes(foundCustomer.value)
        ) {
          setCarbonCopyEmails([...carbonCopyEmails, foundCustomer.value]);
          setSearchCC('');
          e.preventDefault();
          return;
        } else if (foundCustomer) {
          dispatchInvoiceError('Email is already added in Recipient or CC.');
        }

        // Add any input as email (even if not valid)
        if (!carbonCopyEmails.includes(searchCC.trim())) {
          if (!isEmail(searchCC)) {
            dispatchInvoiceError('Please add proper email');
            return;
          }
          setCarbonCopyEmails([...carbonCopyEmails, searchCC.trim()]);
          setSearchCC('');
          e.preventDefault();
        }
      }
    },
    [carbonCopyEmails, customerWithEmailList, recipientEmails, searchCC]
  );

  const handleIncludePDF = useCallback(() => {
    setIsIncludePDF(!isIncludePDF);
  }, [isIncludePDF]);

  return (
    <>
      <ModalHeader title='Send Invoice' onClose={onClickClose} />
      <ModalContent>
        <EmailSection>
          <InputSection>
            <LabelSection>
              <Text>Recipients</Text>
              {!showCCField && <AddCCBlock onClick={() => setShowCCField(true)}>Add CC</AddCCBlock>}
            </LabelSection>
            <EmailName>
              <Dropdown
                ref={recipientRef}
                modalTitle='Recipient'
                isMinWidth={isMinWidth}
                closeOnItemClick={false}
                updateDropdownOpen={(value) => setIsDropdownOpen(value)}
                content={
                  <Input
                    inputType='tags'
                    multiSelectProps={{
                      isActive: isDropdownOpen,
                      placeholderWhenEmpty: 'Write Person or Email',
                      selectedUsers:
                        recipientEmails.map((email, index) => ({
                          id: index,
                          displayName: email
                        })) || [],
                      value: searchRecipient,
                      onChange: (e) => onChangeRecipientInput(e.target.value),
                      onKeyDown: handleRecipientKeyDown,
                      showIcon: true,
                      isDropdownOpen: isDropdownOpen,
                      onIconClick: (e) => {
                        if (!isDropdownOpen) return;
                        e.preventDefault();
                        e.stopPropagation();
                        recipientRef?.current?.closeDropdown();
                      },
                      onRemoveUser: (user) => {
                        const newEmails = recipientEmails.filter((email) => email !== user.displayName);
                        setRecipientEmails(newEmails);
                      }
                    }}
                  />
                }
                trigger='click'>
                {customerWithEmailList
                  ?.filter(
                    (item) => !isEmpty(item?.value) && item.value.toLowerCase().includes(searchRecipient.toLowerCase())
                  )
                  .map((customer, index) => {
                    const isSelected = recipientEmails.includes(customer.value);
                    return (
                      <DropdownItem
                        isChecked={isSelected}
                        key={`${customer.value}-${index}`}
                        label={customer.label}
                        onClick={() => selectRecipentEmail(customer.value)}
                        isSuffix={
                          isSelected ? (
                            <SVGIcon
                              name='checkbox-icon'
                              width={ICON_SIZE.MD}
                              height={ICON_SIZE.MD}
                              viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                            />
                          ) : null
                        }
                      />
                    );
                  })}
              </Dropdown>
            </EmailName>
          </InputSection>
          {/* cc field */}
          {showCCField && (
            <InputSection>
              <LabelSection>
                <Text>CC</Text>
                <AddCCBlock
                  onClick={() => {
                    setCarbonCopyEmails([]);
                    setSearchCC('');
                    setShowCCField(false);
                  }}>
                  Remove
                </AddCCBlock>
              </LabelSection>
              <EmailName>
                <Dropdown
                  ref={ccRef}
                  modalTitle='Recipient'
                  isMinWidth={isMinWidth}
                  closeOnItemClick={false}
                  updateDropdownOpen={(value) => setIsCCDropdownOpen(value)}
                  content={
                    <Input
                      inputType='tags'
                      multiSelectProps={{
                        isActive: isCCDropdownOpen,
                        placeholderWhenEmpty: 'Write Person or Email',
                        selectedUsers:
                          carbonCopyEmails.map((email, index) => ({
                            id: index,
                            displayName: email
                          })) || [],
                        value: searchCC,
                        onChange: (e) => onChangeCCInput(e.target.value),
                        onKeyDown: handleCCKeyDown,
                        showIcon: true,
                        isDropdownOpen: isCCDropdownOpen,
                        onIconClick: (e) => {
                          if (!isCCDropdownOpen) return;
                          e.preventDefault();
                          e.stopPropagation();
                          ccRef?.current?.closeDropdown();
                        },
                        onRemoveUser: (user) => {
                          const newEmails = carbonCopyEmails.filter((email) => email !== user.displayName);
                          setCarbonCopyEmails(newEmails);
                        }
                      }}
                    />
                  }
                  trigger='click'>
                  {customerWithEmailList
                    ?.filter(
                      (item) => !isEmpty(item?.value) && item.value.toLowerCase().includes(searchCC.toLowerCase())
                    )
                    .map((customer, index) => {
                      const isSelected = carbonCopyEmails.includes(customer.value);
                      return (
                        <DropdownItem
                          isChecked={isSelected}
                          key={`${customer.value}-${index}`}
                          label={customer.label}
                          onClick={() => selectCarbonEmail(customer.value)}
                          isSuffix={
                            isSelected ? (
                              <SVGIcon
                                name='checkbox-icon'
                                width={ICON_SIZE.MD}
                                height={ICON_SIZE.MD}
                                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                              />
                            ) : null
                          }
                        />
                      );
                    })}
                </Dropdown>
              </EmailName>
            </InputSection>
          )}
          {/* message subject field */}
          <Input
            label='Message subject'
            placeHolder='Enter message subject'
            value={subjectInput}
            onChange={(e) => setSubjectInput(e.target.value)}
          />
          {/* message body field */}
          <TextArea
            height={'58px'}
            label='Message body'
            placeHolder='Enter your Message'
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
          />
          <Payment>
            {paymentEnabled ? (
              <Check>
                <CheckboxInput
                  id='paymentDisable'
                  name='paymentDisable'
                  checked={isOnlinePayment}
                  onChange={(e) => setIsOnlinePayment(e.target.checked)}
                />
                <label htmlFor='paymentDisable' className='label-checkbox'>
                  Collect Online Payment
                </label>
              </Check>
            ) : (
              <Check>
                <p>
                  <a href={ROUTES.PAYMENT_SETTING}>Configure</a> Stripe or Paypal to collect Payment
                </p>
              </Check>
            )}
          </Payment>
        </EmailSection>
        <ButtonSection>
          <Check role='button' onClick={handleIncludePDF}>
            <CheckboxInput checked={isIncludePDF} />
            <Label>Include a PDF version of the invoice</Label>
          </Check>
          <Buttons>
            <Button title='Cancel' onClick={onClickClose} buttonType='button' variant={BUTTON_TYPE.SECONDARY} />
            <Button
              title='Send Invoice'
              variant={BUTTON_TYPE.PRIMARY}
              onClick={onSubmit}
              buttonType='button'
              isLoading={loading}
              disabled={loading}
            />
          </Buttons>
        </ButtonSection>
      </ModalContent>
    </>
  );
};

export default SendInvoiceModal;
